import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Button, Flex, Grid, Typography } from 'antd';
import { ChevronLeft } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import z from 'zod';

import { AgentTemplateGrid } from '../../../../modules/ai-agent';
import { CreateAiAssistantThread } from '../../../../modules/ai-next/components';
import { useAiAssistant } from '../../../../modules/ai-next/providers';
import { useMeasureHeight, useNavigationPosition } from '../../../../ui/hooks';
import { useLayoutInsets } from '../../../../ui/providers/layout-insets';

const AgentBuilderSearchSchema = z.object({
  draftThreadId: z.uuid().optional().catch(undefined),
});

const AgentBuilderCreateMode = () => {
  const navigate = useNavigate();
  const screens = Grid.useBreakpoint();
  const isMobile = screens.sm === false;
  const { draftThreadId } = Route.useSearch();
  const insets = useLayoutInsets();
  const [headerRef, { height: headerHeight }] = useMeasureHeight<HTMLDivElement>();
  const [value, setValue] = useState('');

  const { uiContext } = useAiAssistant();

  useEffect(() => {
    uiContext.setScopedFlow({ agentBuilder: {} });

    return () => {
      uiContext.clearScopedFlow();
    };
  }, [uiContext]);

  const handleTemplateSelect = useCallback((prompt: string) => {
    setValue(prompt);
  }, []);

  const { isSideNav } = useNavigationPosition();

  return (
    <Flex vertical style={{ height: `calc(100dvh - ${insets.top}px)`, overflow: 'hidden' }}>
      <Flex
        vertical
        style={{
          flex: 1,
          minHeight: 0,
          margin: `${isSideNav ? 16 : 0}px 24px 24px 16px`,
          background: 'var(--ant-color-fill-alter)',
          border: '1px solid var(--ant-color-border)',
          borderRadius: 20,
        }}
      >
        <Flex
          ref={headerRef}
          align="center"
          justify="flex-start"
          gap={12}
          style={{
            padding: '11px 18px',
            borderBottom: '1px solid var(--ant-color-border-secondary)',
          }}
        >
          <Button
            data-testid="agent-back"
            icon={<ChevronLeft size={14} />}
            style={{ fontSize: 12, padding: '0 10px 0 7px', height: 28 }}
            onClick={() => navigate({ to: '/agents' })}
          >
            Agents
          </Button>
          <Typography.Text strong style={{ fontSize: 15 }}>
            Create Agent
          </Typography.Text>
          {screens.sm && (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Describe it, pick a template, or both
            </Typography.Text>
          )}
        </Flex>
        <Flex vertical style={{ height: `calc(100% - ${headerHeight}px)`, minHeight: 0 }}>
          {draftThreadId ? (
            <Flex
              vertical
              style={{
                flex: 1,
                minHeight: 0,
                width: '100%',
                maxWidth: 760,
                margin: '0 auto',
                padding: isMobile ? '16px 16px 0' : '24px 24px 0',
              }}
            >
              <CreateAiAssistantThread
                threadId={draftThreadId}
                value={value}
                onChange={setValue}
                placeholder="Describe the agent you want to build or select a template above..."
                onCreated={(threadId) =>
                  navigate({ to: '/agents/new', search: { draftThreadId: threadId }, replace: true })
                }
              />
            </Flex>
          ) : (
            <>
              <Flex
                vertical
                style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: isMobile ? '20px 16px 0' : '24px 24px 0' }}
              >
                <Flex vertical gap={24} style={{ minHeight: 0, maxWidth: 760, margin: '0 auto', width: '100%' }}>
                  <Flex vertical align="center" gap={28} style={{ minHeight: 0, width: '100%' }}>
                    {/* TODO: re-enable <HowAgentsWorkVideo /> once the updated walkthrough is recorded */}
                    <Flex vertical align="center" style={{ textAlign: 'center', padding: '0 8px' }}>
                      <Typography.Text strong style={{ fontSize: isMobile ? 20 : 23, textAlign: 'center' }}>
                        What should your agent do?
                      </Typography.Text>
                      <Typography.Text type="secondary" style={{ fontSize: isMobile ? 13 : 14, textAlign: 'center' }}>
                        Describe it in your own words below, or start from a template.
                      </Typography.Text>
                    </Flex>
                    <AgentTemplateGrid onSelect={handleTemplateSelect} />
                  </Flex>
                </Flex>
              </Flex>
              <div style={{ padding: isMobile ? '12px 16px 16px' : '12px 24px 24px' }}>
                <div style={{ maxWidth: 760, margin: '0 auto' }}>
                  <CreateAiAssistantThread
                    threadId={null}
                    value={value}
                    onChange={setValue}
                    placeholder="Describe the agent you want to build or select a template above..."
                    onCreated={(threadId) =>
                      navigate({ to: '/agents/new', search: { draftThreadId: threadId }, replace: true })
                    }
                  />
                </div>
              </div>
            </>
          )}
        </Flex>
      </Flex>
    </Flex>
  );
};

export const Route = createFileRoute('/_authenticated/_app/agents/new')({
  component: AgentBuilderCreateMode,
  validateSearch: (params) => AgentBuilderSearchSchema.parse(params),
});
